import { UnappliedPaymentsGridComponent } from './unapplied-payments-grid/unapplied-payments-grid.component';
import { UnappliedPaymentSearchResultDto } from './../shared/unapplied-payment/dto/unapplied-payment-search-result.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { ProfileService } from 'src/app/profiles/shared/profile.service';
import { ReceiptService } from './../shared/receipt/receipt.service';
import { TitleCasePipe } from './../../shared/pipes/titlecase.pipe';
import { SerialSearchExpression } from 'src/app/shared/search-expression/serial-search-expression.model';
import { InvoiceAdvancedSearch } from './../shared/invoice/invoice-advanced-search.model';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { MenuItem } from 'primeng/api';
import { LazyLoadEvent } from 'primeng/api';
import { InvoiceService } from './../shared/invoice/invoice.service';
import { InvoiceAdvancedSearchResult } from './../shared/invoice/invoice-advanced-search-result.model';
import { Component, OnInit, Input, ViewEncapsulation, OnChanges, ViewChild } from '@angular/core';
import { PagedResult } from 'src/app/shared/model/generic-paged-result.model';
import { DatagridService, TranslationService, NotificationService, LocalStorageService, AuthService } from 'src/app/shared';
import { FieldValueOperatorEnum } from 'src/app/shared/search-expression/field-value-operator.enum';
import { HtmlTemplate } from 'src/app/templates/shared/html-template.model';
import { BinaryOperatorEnum } from 'src/app/shared/search-expression/binary-operator.enum';
import { WlUpperCaseFirstLetterPipe } from './../../shared/pipes/ucfirst.pipe';
import { ConfigService } from 'src/app/config/shared/config.service';
import { BetaFeatureToggleEnum } from 'src/app/config/shared/constants/beta-feature-toggle.enum';
import { ShareLinkDialogComponent } from 'src/app/shared/component/share-link-dialog/share-link-dialog.component';

@Component({
  selector: 'wl-transactions-grid',
  templateUrl: './transactions-grid.component.html',
  styleUrls: ['./transactions-grid.component.less'],
  providers: [TitleCasePipe],
  encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TransactionsGridComponent implements OnInit, OnChanges {
  @Input() searchParams: InvoiceAdvancedSearch;
  @Input() profileId?: number;

  public gridMenuItems: MenuItem[];
  public searchResult: PagedResult<InvoiceAdvancedSearchResult[]>;
  public htmlTemplates: HtmlTemplate[];
  public fromDate: Date;
  public toDate: Date;
  public loading: boolean;
  public rowMenuItems: Map<number, MenuItem[]>;
  public invoiceSearchToEmail: InvoiceAdvancedSearch;
  public selectAll: boolean;
  public selectedInvoices: { [invoiceNum: number]: boolean } = {};
  public selectedInvoicesCount: number;
  public headerCheckboxIsIndeterminate: boolean;
  public loadingInvoiceNumbersForPayment: boolean;
  public loadingInvoiceNumbersForApplyUnappliedPayments: boolean;
  public applyPaymentInvoiceNumbers: number[];
  public profileReportName: string;
  public unappliedPaymentResult: UnappliedPaymentSearchResultDto;
  public showApplyPaymentDialog: boolean;
  public showEmailDialog: boolean;
  public showReceiptConfirmation = false;
  public showCreateUnappliedPaymentDialog = false;
  public showApplyUnappliedPaymentsDialog = false;
  public showSelectInvoicesDialog = false;

  @ViewChild(UnappliedPaymentsGridComponent) private _unappliedPaymentsComponent: UnappliedPaymentsGridComponent;
  @ViewChild(ShareLinkDialogComponent) private _shareLinkDialog: ShareLinkDialogComponent;

  constructor(
    private _invoiceService: InvoiceService,
    private _datagridService: DatagridService,
    private _translationService: TranslationService,
    private _navigationService: NavigationService,
    private _ucFirstPipe: WlUpperCaseFirstLetterPipe,
    private _titleCasePipe: TitleCasePipe,
    private _receiptService: ReceiptService,
    private _profileService: ProfileService,
    private _notificationService: NotificationService,
    private _localStorageService: LocalStorageService,
    private _authService: AuthService,
    private _configService: ConfigService
  ) {
    this.searchResult = new PagedResult([]);
    this.rowMenuItems = new Map();
    this.selectedInvoicesCount = 0;
  }

  async ngOnInit() {
    if (this.searchParams) {
      this.initDates();
    }
    Promise.all([
      this._translationService.get('revenue.transactions-grid.print'),
      this._translationService.get('revenue.transactions-grid.email'),
      this._translationService.get('revenue.transactions-grid.print-statement'),
      this._translationService.get('revenue.transactions-grid.online-bill-pay'),
      this._receiptService.GetAutomaticReciptSettings(),
    ]).then(([printString, emailString, printStatementString, viewBillPayString, automaticReceiptSettings]) => {
      this.gridMenuItems = [
        {
          label: this._ucFirstPipe.transform(printString),
          icon: 'pi pi-print',
          command: () => this.printActionSelected(),
        },
        {
          label: this._ucFirstPipe.transform(emailString),
          icon: 'pi pi-envelope',
          command: () => this.emailActionSelected(),
        },
      ];
      if (this.profileId) {
        this.gridMenuItems.push({
          label: this._titleCasePipe.transform(printStatementString),
          icon: 'pi pi-print',
          command: () => this.printStatementActionSelected(),
        });
        this.gridMenuItems.push({
          label: this._titleCasePipe.transform(viewBillPayString),
          icon: 'pi pi-dollar',
          command: () => this.onlineBillPayActionSelected(),
        });
      }
    });
  }

  ngOnChanges() {
    if (this.searchParams) {
      this.initDates();
      this.search();
    }

    if (this.profileId != null) {
      this._profileService.getProfile(this.profileId).then((data) => {
        this.profileReportName = data.ReportName;
      });
    }
  }

  lazyLoadInvoices(event: LazyLoadEvent) {
    if (this.searchParams) {
      this._datagridService.mapDatagridPagingToPagedSearch(event, this.searchParams);
      this.search();
    }
  }

  onDateSelection() {
    this.removeDateExpression();
    this.addDateExpression();
    this.rememberDateExpression();
    this.search();
  }

  actionItemsForInvoice(invoice: InvoiceAdvancedSearchResult): MenuItem[] {
    if (!this.rowMenuItems.has(invoice.InvoiceNum)) {
      this.rowMenuItems.set(invoice.InvoiceNum, [
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('revenue.transactions-grid.print')),
          icon: 'pi pi-print',
          command: () => this.printActionSelected(invoice.InvoiceNum),
        },
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('revenue.transactions-grid.email')),
          icon: 'pi pi-envelope',
          command: () => this.emailActionSelected(invoice.InvoiceNum),
        },
      ]);
      if (invoice.AmountPaid > 0) {
        this.rowMenuItems.get(invoice.InvoiceNum).push({
          label: this._ucFirstPipe.transform(this._translationService.getInstant('revenue.transactions-grid.send-receipt')),
          icon: 'pi pi-envelope',
          command: () => this.sendReceipt(invoice.InvoiceNum),
        });

        this.rowMenuItems.get(invoice.InvoiceNum).push({
          label: this._translationService.getInstantCapitalizeFirstCharacter('revenue.transactions-grid.print-receipt'),
          icon: 'pi pi-print',
          command: () => this._navigationService.navigateToPaymentReceiptForPaymentHeader(invoice.PaymentHeaderIds[0]),
        });
      }

      this.rowMenuItems.get(invoice.InvoiceNum).push({
        label: this._ucFirstPipe.transform(this._translationService.getInstant('revenue.transactions-grid.share-link')),
        icon: 'pi pi-share-alt',
        command: () => this.openShareLinkDialog(invoice.InvoiceNum),
      });
    }
    return this.rowMenuItems.get(invoice.InvoiceNum);
  }

  onSelectAllChange() {
    if (this.selectAll) {
      this.selectedInvoicesCount = this.searchResult.TotalCount;
    } else {
      this.selectedInvoicesCount = 0;
    }
    Object.keys(this.selectedInvoices).forEach((key) => (this.selectedInvoices[key] = this.selectAll));
    this.searchResult.Result.forEach((result) => {
      this.selectedInvoices[result.InvoiceNum] = this.selectAll;
    });
    this.checkForIndeterminate();
  }

  onRowSelectionChange(invoiceNum: number) {
    if (this.selectedInvoices[invoiceNum]) {
      this.selectedInvoicesCount += 1;
    } else {
      this.selectedInvoicesCount -= 1;
    }
    this.checkForIndeterminate();
  }

  applyPaymentClicked() {
    this.loadingInvoiceNumbersForPayment = true;
    const getAll = this.selectedInvoicesCount === 0;
    this.getAllSelectedInvoiceNumbers(getAll).then((numbers) => {
      this.applyPaymentInvoiceNumbers = numbers;
      this.showApplyPaymentDialog = true;
      this.loadingInvoiceNumbersForPayment = false;
    });
  }

  applyUnappliedPaymentsClicked() {
    this.loadingInvoiceNumbersForApplyUnappliedPayments = true;
    this.getAllSelectedInvoiceNumbers(true).then((numbers) => {
      this.applyPaymentInvoiceNumbers = numbers;
      this.showApplyUnappliedPaymentsDialog = true;
      this.loadingInvoiceNumbersForApplyUnappliedPayments = false;
    });
  }

  refreshUnappliedPayments() {
    this._unappliedPaymentsComponent.search();
  }

  public search() {
    this.loading = true;
    this._invoiceService.advancedSearch(this.searchParams).then((result) => {
      this.searchParams.TotalCount = result.TotalCount;
      this.searchResult = result;
      if (this.selectAll) {
        this.searchResult.Result.forEach((invoiceResult) => {
          if (this.selectedInvoices[invoiceResult.InvoiceNum] === undefined) {
            this.selectedInvoices[invoiceResult.InvoiceNum] = this.selectAll;
          }
        });
      }
      this.loading = false;
    });
  }

  public sendReceipt(invoiceNum: number) {
    this._receiptService
      .SendReceipt(invoiceNum)
      .then(() => {
        this.showReceiptConfirmation = true;
      })
      .catch((error) => {
        this._notificationService.httpError(error);
      });
  }

  public closeReceiptConfirmation() {
    this.showReceiptConfirmation = false;
  }

  // this function exists to check whether the header checkbox should be in an indeterminate state
  private checkForIndeterminate() {
    let indeterminate = false;
    if (this.selectAll) {
      Object.keys(this.selectedInvoices).forEach((key) => {
        if (!this.selectedInvoices[key]) {
          indeterminate = true;
        }
      });
    } else {
      Object.keys(this.selectedInvoices).forEach((key) => {
        if (this.selectedInvoices[key]) {
          indeterminate = true;
        }
      });
    }
    this.headerCheckboxIsIndeterminate = indeterminate;
  }

  private printActionSelected(invoiceNum?: number) {
    if (invoiceNum) {
      this._navigationService.navigateToInvoiceReport(invoiceNum);
    } else {
      this.getAllSelectedInvoiceNumbers().then((numbers) => {
        this._navigationService.navigateToInvoiceReportForMultipleNumbers(numbers);
      });
    }
  }

  private emailActionSelected(invoiceNum?: number) {
    if (!invoiceNum && Object.keys(this.selectedInvoices)?.length <= 0 && !this.selectAll) {
      this.showSelectInvoicesDialog = true;
    } else {
      this.showEmailDialog = true;
      this.invoiceSearchToEmail = this.buildInvoiceSearch(invoiceNum);
    }
  }

  private printStatementActionSelected() {
    this._navigationService.navigateToStatementForProfileId(this.profileId);
  }

  private onlineBillPayActionSelected() {
    this._configService.hasBetaFeatureEnabled(BetaFeatureToggleEnum.MemberPortal).then((hasToggle) => {
      if (hasToggle) {
        const portalUrl = this._authService.getPortalUrlWithAccessTokenHash(`/atlas/portal/open-invoices?profileId=${this.profileId}`);
        this._navigationService.popupSafeNavigate(portalUrl);
      } else {
        this._navigationService.navigateToOnlineBillPay(this.profileId);
      }
    });
  }

  private getAllSelectedInvoiceNumbers(getAll = false) {
    if (this.selectAll || getAll) {
      const copyParams: InvoiceAdvancedSearch = JSON.parse(JSON.stringify(this.searchParams));
      copyParams.PageSize = 0; // get all
      return this._invoiceService.advancedSearch(copyParams).then((searchResult) => {
        // get invoice numbers from result and remove any deselected invoices
        const invoiceNumbers = searchResult.Result.map((x) => x.InvoiceNum).filter(
          (num) => this.selectedInvoices[num] || this.selectedInvoices[num] === undefined
        );
        return Promise.resolve(invoiceNumbers);
      });
    } else {
      const invoiceNumbers: number[] = [];
      Object.keys(this.selectedInvoices).forEach((invoiceNumber) => {
        if (this.selectedInvoices[invoiceNumber]) {
          invoiceNumbers.push(parseInt(invoiceNumber, 10));
        }
      });
      return Promise.resolve(invoiceNumbers);
    }
  }

  private buildInvoiceSearch(invoiceNum?: number) {
    let invoiceAdvancedSearch = new InvoiceAdvancedSearch();
    if (!invoiceNum) {
      // multi-select action
      if (this.selectAll) {
        invoiceAdvancedSearch = this.searchParams;
        invoiceAdvancedSearch.PageSize = 0;
        Object.keys(this.selectedInvoices).forEach((selectedInvoiceNum) => {
          if (!this.selectedInvoices[selectedInvoiceNum]) {
            // exclude invoice from select all
            const expression = new SerialSearchExpression();
            expression.Field = 'InvoiceNum';
            expression.Value = selectedInvoiceNum;
            expression.FieldValueOperator = FieldValueOperatorEnum.NotEqual;
            if (invoiceAdvancedSearch.InvoiceInfoSearchExpressions.length > 0) {
              expression.BinaryOperator = BinaryOperatorEnum.And;
            }
            invoiceAdvancedSearch.InvoiceInfoSearchExpressions.push(expression);
          }
        });
      } else {
        Object.keys(this.selectedInvoices).forEach((selectedInvoiceNum) => {
          if (this.selectedInvoices[selectedInvoiceNum]) {
            // exclude invoice from select all
            const expression = new SerialSearchExpression();
            expression.Field = 'InvoiceNum';
            expression.Value = selectedInvoiceNum;
            if (invoiceAdvancedSearch.InvoiceInfoSearchExpressions.length > 0) {
              expression.BinaryOperator = BinaryOperatorEnum.Or;
            }
            invoiceAdvancedSearch.InvoiceInfoSearchExpressions.push(expression);
          }
        });
      }
    } else {
      // action on row menu
      const expression = new SerialSearchExpression();
      expression.Field = 'InvoiceNum';
      expression.Value = invoiceNum.toString();
      invoiceAdvancedSearch.InvoiceInfoSearchExpressions.push(expression);
    }
    return invoiceAdvancedSearch;
  }

  private initDates() {
    for (const expression of this.searchParams.InvoiceInfoSearchExpressions) {
      if (expression.Field.toLowerCase() === 'invoicedate') {
        this.fromDate = new Date(expression.Value);
        this.toDate = new Date(expression.Value2);
        return;
      }
    }
    // if search params don't include them, use default dates
    const today = new Date();
    this.fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    this.toDate = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());

    // use stored params, if any
    const data: any = this._localStorageService.getPerishableObject('revenue.trasation-grid.dates');
    if (data) {
      this.fromDate = new Date(data.fromDate);
      this.toDate = new Date(data.toDate);
    }

    this.addDateExpression();
  }

  private removeDateExpression() {
    this.searchParams.InvoiceInfoSearchExpressions = this.searchParams.InvoiceInfoSearchExpressions.filter(
      (x) => x.Field.toLowerCase() !== 'invoicedate'
    );
  }

  private addDateExpression() {
    const dateExpression = new SerialSearchExpression();
    dateExpression.Field = 'InvoiceDate';
    dateExpression.Value = `${this.fromDate.getFullYear()}-${this.fromDate.getMonth() + 1}-${this.fromDate.getDate()} 00:00`;
    dateExpression.Value2 = `${this.toDate.getFullYear()}-${this.toDate.getMonth() + 1}-${this.toDate.getDate()} 23:59`;
    dateExpression.FieldValueOperator = FieldValueOperatorEnum.Between;
    this.searchParams.InvoiceInfoSearchExpressions.push(dateExpression);
  }

  private rememberDateExpression() {
    const dateValues = { fromDate: this.fromDate, toDate: this.toDate };
    const expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + 30);
    this._localStorageService.setPerishableObject('revenue.trasation-grid.dates', dateValues, expirationDate);
  }

  private openShareLinkDialog(invoiceNumber: number) {
    const linkCallback: () => Promise<string> = async () =>
      this._profileService.getProfileAccessGuid(this.profileId).then((guidDto) => {
        return this._navigationService.getShareInvoiceLink(guidDto.Guid, invoiceNumber);
      });

    this._shareLinkDialog.open(linkCallback);
  }
}
